/**
 * 공종 템플릿 저장 입력.
 *
 * 화면이 보내는 것은 **공종 코드와 항목 트리뿐**이다 — 그룹·이름·순서는
 * `TRADES` 가 정하는 값이라 화면이 보낸 것을 믿지 않고 코드로 다시 세운다.
 * (스타일 공사를 기본 공사 그룹으로 보내면 견적서의 소계가 어긋난다.)
 *
 * 깊이는 두 단계까지다 — SET 부모와 그 자식. 편집기가 그 이상 만들지 않고,
 * 견적 편집기도 두 단계만 그린다.
 */

import { z } from "zod";

import { topicJosa } from "../../lib/josa";
import { evalQtyExpr, isExpr, usesArea } from "../quote/qty-expr";
import { TEMPLATE_LIMITS } from "./from-quote";
import { TradeCode, TradeGroup } from "./trades";
import { TemplateItem, TemplateTrade } from "./types";

/** 템플릿 수량식의 오류 문구. 「평」 이 있으면 평수를 바꿔 봐도 늘 나는 오류만 막는다(평수는 견적마다 다르다). 없으면 null. */
export const templateQtyExprError = (raw: string): string | null => {
  const text = raw.trim();
  if (text === "") return null;
  const probe = evalQtyExpr(text, { area_pyeong: 1 });
  if (probe.ok) return null;
  if (!isExpr(text) || !usesArea(text)) return probe.message;
  if (probe.kind === "syntax" || probe.kind === "paren" || probe.kind === "range") return probe.message;
  // 0 나누기는 평수 2 에서도, 음수는 평수 1000 에서도 나면 평수와 무관한 오류다.
  const other = evalQtyExpr(text, { area_pyeong: probe.kind === "negative" ? 1_000 : 2 });
  return !other.ok && other.kind === probe.kind ? probe.message : null;
};

/**
 * 칸 하나. **빈 값은 빈 문자열로 모은다** — 시드 템플릿에는 규격이 `null` 인 줄이
 * 있고(21줄), 편집기는 빈 칸을 `""` 로 보낸다. 둘을 다르게 두면 같은 줄을
 * 저장만 해도 모양이 바뀐다.
 */
const line = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, { message: `${label}${topicJosa(label)} ${max}자를 넘을 수 없습니다` })
    .nullish()
    .transform((value) => value ?? "");

const leafSchema = z.object({
  scope: line(TEMPLATE_LIMITS.scope, "범위"),
  item: line(TEMPLATE_LIMITS.item, "작업내역"),
  unit: line(TEMPLATE_LIMITS.unit, "규격"),
  // 견적 항목(0021)과 같은 200자.
  qty_expr: line(200, "수량식").superRefine((value, ctx) => {
    const error = templateQtyExprError(value);
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `수량식 「${value}」 — ${error}` });
  }),
});

const itemSchema = leafSchema.extend({
  children: z
    .array(leafSchema)
    .max(TEMPLATE_LIMITS.children, { message: `SET 자식은 ${TEMPLATE_LIMITS.children}개까지입니다` })
    .default([]),
});

const tradeSchema = z.object({
  /**
   * 내장 15개로 좁히지 않는다 — 회사가 추가한 공종이 템플릿에 들어온다.
   * 「이 회사가 쓸 수 있는 코드인가」 는 목록을 들고 있는 액션이 본다.
   */
  trade: z
    .string()
    .trim()
    .min(1, { message: "공종 코드가 비어 있습니다" })
    .max(60, { message: "공종 코드가 너무 깁니다" }),
  items: z
    .array(itemSchema)
    .max(TEMPLATE_LIMITS.itemsPerTrade, { message: `한 공종에 항목은 ${TEMPLATE_LIMITS.itemsPerTrade}개까지입니다` })
    .default([]),
});

export const templateSaveSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: "템플릿 이름을 입력하세요" })
    .max(60, { message: "템플릿 이름은 60자를 넘을 수 없습니다" }),
  is_default: z.boolean(),
  trades: z
    .array(tradeSchema)
    // 내장 15 + 회사 추가분. 무제한으로 두면 견적서가 읽히지 않는다.
    .max(TEMPLATE_LIMITS.trades, { message: `공종은 ${TEMPLATE_LIMITS.trades}개까지입니다` })
    .superRefine((rows, ctx) => {
      const seen = new Set<string>();
      for (const row of rows) {
        if (seen.has(row.trade)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `공종이 겹칩니다 — ${row.trade}`,
          });
          return;
        }
        seen.add(row.trade);
      }
    }),
}).superRefine((input, ctx) => {
  // 기본 템플릿은 새 견적의 뼈대가 된다. 공종이 하나도 없으면 「기본」 을 지정한
  // 조직이 빈 견적서를 받는다 — 저장 자리에서 막는다.
  if (input.is_default && input.trades.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["trades"],
      message: "기본 템플릿에는 공종이 하나 이상 있어야 합니다",
    });
  }
});

export type TemplateSaveParsed = z.output<typeof templateSaveSchema>;

/**
 * 저장 모양 → 저장소가 받는 모양.
 *
 * `sort_order` 는 화면에서 끌어 놓은 **그 순서**다(1부터). 공종의 고유 번호가
 * 아니라 이 템플릿 안에서의 자리라, 공종을 빼도 빈 번호가 남지 않는다.
 * 자식이 없는 줄은 `children` 을 아예 두지 않는다 — 빈 배열은 「SET 인데 비었다」
 * 로 읽힌다.
 */
export const toTemplateTrades = (
  parsed: TemplateSaveParsed,
  /** 이 회사가 쓸 수 있는 공종. 이름·비용 분류를 여기서 가져온다. */
  tradeRows: readonly { code: string; name: string; group: TradeGroup }[]
): TemplateTrade[] =>
  parsed.trades.map((row, index): TemplateTrade => {
    const def =
      tradeRows.find((t) => t.code === row.trade) ??
      (() => {
        throw new Error(`알 수 없는 공종입니다 — ${row.trade}`);
      })();
    return {
      group: def.group,
      trade: def.code,
      name: def.name,
      sort_order: index + 1,
      items: row.items.map((item): TemplateItem => {
        const children = item.children.map(
          (child): TemplateItem => ({
            scope: child.scope,
            item: child.item,
            unit: child.unit,
            ...(child.qty_expr ? { qty_expr: child.qty_expr } : {}),
          })
        );
        // SET 부모는 수량이 자식 합이라 식을 싣지 않는다.
        return children.length > 0
          ? { scope: item.scope, item: item.item, unit: item.unit, children }
          : {
              scope: item.scope,
              item: item.item,
              unit: item.unit,
              ...(item.qty_expr ? { qty_expr: item.qty_expr } : {}),
            };
      }),
    };
  });
